import React, { useEffect, useState } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { getYears } from '../../../../../../utils/commonFunctions';

import dayjs from 'dayjs';
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

interface ModelConfigDatetimeInputProps {
    onChange: (value: number) => void; // epoch in seconds
    value: number; // epoch in seconds
    disabled?: boolean;
}

const ModelConfigDatetimeInput: React.FC<ModelConfigDatetimeInputProps> = ({
    onChange,
    value,
    disabled = false
}): JSX.Element => {
    const [time, setTime] = useState<null | dayjs.Dayjs>(value > 0 ? dayjs(value * 1000).utc() : null);

    const onInputChange = (newValue: any) => {
        if (newValue) {
            const parsed = dayjs.utc(newValue);
            setTime(parsed);
            if (parsed.isValid() && parsed.unix() > 0) {
                onChange(parsed.unix());
            } else {
                onChange(-1);
            }
        } else {
            setTime(null);
            onChange(-1);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

        const input = event.currentTarget;
        const start = input.selectionStart ?? 0;
        
        let currentDayjs = time && time.isValid() ? time : dayjs().utc();
        const diff = event.key === 'ArrowUp' ? 1 : -1;

        // Determine segment by index: YYYY-MM-DD HH:mm:ss
        if (start >= 0 && start <= 4) {
            currentDayjs = currentDayjs.add(diff, 'year');
        } else if (start >= 5 && start <= 7) {
            currentDayjs = currentDayjs.add(diff, 'month');
        } else if (start >= 8 && start <= 10) {
            currentDayjs = currentDayjs.add(diff, 'day');
        } else if (start >= 11 && start <= 13) {
            currentDayjs = currentDayjs.add(diff, 'hour');
        } else if (start >= 14 && start <= 16) {
            currentDayjs = currentDayjs.add(diff, 'minute');
        } else if (start >= 17 && start <= 19) {
            currentDayjs = currentDayjs.add(diff, 'second');
        } else {
            return;
        }

        const newYear = currentDayjs.year();
        if (newYear >= 2000 && newYear <= 2037) {
            event.preventDefault();
            onInputChange(currentDayjs);
            setTimeout(() => {
                input.setSelectionRange(start, start);
            }, 0);
        } else {
            event.preventDefault();
        }
    };

    useEffect(() => {
        if (value > 0) {
            setTime(dayjs(value * 1000).utc());
        } else {
            setTime(null);
        }
    }, [value]);

    return (
        <div className={`d-flex align-items-center rounded-1 overflow-hidden w-100 p-0 ${disabled ? 'opacity-75 pointer-events-none cursor-auto' : ''}`}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                    timezone="UTC"
                    disabled={disabled}
                    slotProps={{
                        textField: {
                            onKeyDown: handleKeyDown
                        }
                    }}
                    sx={{
                        borderRadius: '0px',
                        background: 'transparent !important',
                        '.MuiFormControl-root': {
                            paddingRight: '0px',
                        },
                        '.MuiInputBase-input': {
                            fontSize: '12px !important',
                        },
                        '.MuiOutlinedInput-input': {
                            fontSize: '12px !important',
                            padding: '0 8px !important',
                        },
                        '.MuiOutlinedInput-root': {
                            height: '24px !important',
                            paddingRight: '0px'
                        },
                        '.MuiInputAdornment-root': {
                            display: 'none',
                        },
                        '.MuiOutlinedInput-notchedOutline': {
                            borderRadius: '0px',
                            border: 'none',
                            borderRight: 'none !important',
                        },
                        '.Mui-disabled': {
                            color: 'rgba(240,240,240,0.30) !important',
                            '-webkit-text-fill-color ': 'rgba(240,240,240,0.30) !important',
                            cursor: 'auto'
                        }
                    }}
                    className="w-100"
                    ampm={false}
                    format='YYYY-MM-DD HH:mm:ss'
                    views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                    shouldDisableYear={(date) => {
                        const current_year = dayjs(date).year();
                        const yearList = getYears(2000, 2037);
                        return !yearList.includes(current_year);
                    }}
                    value={time ?? null}
                    onChange={onInputChange}
                />
            </LocalizationProvider>
        </div>
    );
};

export default ModelConfigDatetimeInput;
